// frontend/src/features/onboarding/steps/CreateTankStep.tsx
//
// Wizard step 1 — Welcome + create your tank (Task 16).
//
// This is the first thing a brand-new user sees. Everything in Reef DataHub
// (devices, tests, tasks) belongs to a tank, so tank creation is REQUIRED — the
// app is non-functional without one, and there is no skip on this step.
//
// The form fields/validation mirror the create-tank modal in TanksPage.tsx
// (name required; volume + unit + description optional). The field set is small
// and low-risk to duplicate, so it is kept inline here rather than extracted into
// a shared TankForm (TanksPage's modal also drives edit with an is_default
// checkbox and shared state — extracting would widen the blast radius beyond this
// task for little gain).
import { useState } from 'react';
import {
  Alert,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTank } from '@/contexts/TankContext';
import { useOnboarding } from '@/hooks/useOnboarding';
import { trackOnboarding } from '@/lib/onboardingAnalytics';
import type { CreateTankData } from '@/types/tank';
import type { VolumeUnit } from '@/types/auth';
import { useOnboardingWizard } from '../WizardContext';

const VOLUME_UNIT_OPTIONS = [
  { value: 'litres', label: 'Litres' },
  { value: 'gallons', label: 'Gallons' },
];

export function CreateTankStep() {
  const { user } = useAuth();
  const { createTank } = useTank();
  const { update } = useOnboarding();
  const { next } = useOnboardingWizard();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [volume, setVolume] = useState<number | string>('');
  const [volumeUnit, setVolumeUnit] = useState<VolumeUnit>(user?.volume_unit || 'litres');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayName = user?.display_name?.trim();
  const heading = displayName ? `Welcome, ${displayName}!` : 'Welcome to Reef DataHub!';

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!name.trim()) {
      setError('Tank name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const data: CreateTankData = {
        name: name.trim(),
        description: description.trim() || undefined,
        volume: typeof volume === 'number' ? volume : undefined,
        volume_unit: typeof volume === 'number' ? volumeUnit : undefined,
      };

      await createTank(data);

      // Funnel: the tank was created — step 1 is complete (design §3.7).
      trackOnboarding('step_completed', { step: 'create-tank' });

      // Persist wizard progress fire-and-forget — do not block the UI on it
      // (useGettingStarted / useOnboarding convention: swallow failures).
      update({ step: 1 }).catch(() => {});

      // Advance to the capability step. createTank auto-selects the first tank,
      // so subsequent steps have a selected tank in context.
      next();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tank');
      setIsSubmitting(false);
    }
  };

  return (
    <form
      data-testid="onboarding-step-create-tank"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={1}>{heading}</Title>
          <Text c="dimmed">
            Let's start by creating your tank. Everything in Reef DataHub — devices, tests,
            tasks — belongs to a tank, so this is where it all begins.
          </Text>
        </Stack>

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        <TextInput
          label="Tank Name"
          placeholder="My Reef Tank"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isSubmitting}
          autoFocus
        />

        <Textarea
          label="Description"
          placeholder="Optional description..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isSubmitting}
          rows={2}
        />

        <Group grow>
          <NumberInput
            label="Tank Volume"
            placeholder="e.g. 100"
            min={0}
            decimalScale={1}
            value={volume}
            onChange={setVolume}
            disabled={isSubmitting}
          />
          <Select
            label="Unit"
            data={VOLUME_UNIT_OPTIONS}
            value={volumeUnit}
            onChange={(val) => setVolumeUnit((val as VolumeUnit) || 'litres')}
            disabled={isSubmitting}
          />
        </Group>

        <Group justify="flex-end">
          <Button type="submit" loading={isSubmitting}>
            Create your tank
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
